export const NS = 1_000_000_000;

export function formatTimecode(ns: number, opts: { ms?: boolean } = {}): string {
  const negative = ns < 0;
  const total = Math.abs(ns);
  const s = Math.floor(total / NS);
  const ms = Math.floor((total % NS) / 1_000_000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (v: number, n = 2) => String(v).padStart(n, "0");
  const base = h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
  return `${negative ? "-" : ""}${base}${opts.ms === false ? "" : "." + pad(ms, 3)}`;
}

export function formatDuration(ns: number | null | undefined): string {
  if (ns == null) return "—";
  return formatTimecode(ns, { ms: false });
}

export function formatMs(ns: number | null | undefined, digits = 1): string {
  if (ns == null || Number.isNaN(ns)) return "—";
  return `${(ns / 1e6).toFixed(digits)} ms`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatWallClock(ns: number | null | undefined): string {
  if (!ns) return "—";
  const d = new Date(ns / 1e6);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export function formatRelative(ns: number | null | undefined): string {
  if (!ns) return "—";
  const diff = Date.now() - ns / 1e6;
  const mins = diff / 60000;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.floor(mins)} min ago`;
  const hours = mins / 60;
  if (hours < 24) return `${Math.floor(hours)} h ago`;
  const days = hours / 24;
  if (days < 30) return `${Math.floor(days)} d ago`;
  return formatWallClock(ns);
}

/** Index of the entry nearest `value` in an ascending array. */
export function nearestIndex(sorted: number[], value: number): number {
  if (sorted.length === 0) return -1;
  let lo = 0;
  let hi = sorted.length - 1;
  if (value <= sorted[0]) return 0;
  if (value >= sorted[hi]) return hi;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] === value) return mid;
    if (sorted[mid] < value) lo = mid + 1;
    else hi = mid - 1;
  }
  // lo is the first index above value
  return value - sorted[hi] <= sorted[lo] - value ? hi : lo;
}

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
